import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Box, BoxProps, useMultiStyleConfig, Text, VStack, Skeleton} from '@chakra-ui/react'
import {Button, FormPrivacyDisclaimer} from '_uiComponents'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useForm} from 'react-hook-form'
import {FormRequestSize, ProductVariant, SubmitRequestSizeResponse} from '_types'
import {useCustomApi, useTencentCaptcha, useCurrentCustomer, useAnalytics} from '_customHooks'
import {FormattedMessage, useIntl} from 'react-intl'
import {FormFactory, FormValues} from '_factories'
import {useLoader, useGlobalConfigState} from '_context'

interface ProductRequestSizeProps {
    customer?: unknown
    productId: string
    variantsNotAvailable: ProductVariant[]
    step: number
    onSubmitForm: () => void
}

/**
 * @name ProductRequestSize
 *
 * @component
 *
 * @description
 * Renders the size selector for a product
 *
 * @returns {React.FC} - The rendered product size selector component.
 */
const ProductRequestSize: React.FC<ProductRequestSizeProps> = ({
    customer,
    step,
    productId,
    variantsNotAvailable,
    onSubmitForm,
}: ProductRequestSizeProps & {step: number}) => {
    const {formatMessage} = useIntl()

    const {executeRecaptcha} = useGoogleReCaptcha()
    const {isTencentEnabled, isTencentReady, tencentCaptchaResult, triggerTencentCaptcha, resetTencentCaptcha} = useTencentCaptcha()
    const {locale} = useMultiSite()
    const {showLoader, hideLoader} = useLoader()
    const {appPreferences} = useGlobalConfigState()
    const {useGetForm, useSubmitForm} = useCustomApi()
    const {analyticsEventRequestSize} = useAnalytics()

    const {mutateAsync: getFormMutation} = useGetForm()
    const {mutateAsync: submitFormMutation} = useSubmitForm()
    const isRecaptchaActive = !!appPreferences.apikeyv3_recaptcha

    const [requestSizeForm, setRequestSizeForm] = useState<FormRequestSize>()
    const [isSubmitError, setIsSubmitError] = useState<boolean>(false)

    const [stepState, setStepState] = useState<number>(step)
    const [variant, setVariant] = useState<ProductVariant | null>()

    const recaptchaToken = useRef<string>()
    const tencentToken = useRef<string>()
    const tencentRandstr = useRef<string>()
    const pendingFormData = useRef<FormValues | null>(null)

    const {
        control,
        formState: {errors},
        handleSubmit,
        setValue,
    } = useForm<FormValues>()

    const styles = useMultiStyleConfig('ProductRequestSize', {}) as Record<string, BoxProps>

    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            return
        }
        const googleReCaptchaToken = await executeRecaptcha('ProductRequestSize')
        if (!recaptchaToken.current) recaptchaToken.current = googleReCaptchaToken
    }, [executeRecaptcha])

    const triggerTencentValidation = () => {
        if (isTencentReady) {
            triggerTencentCaptcha()
        }
    }

    const resetCaptchaTokens = () => {
        recaptchaToken.current = ''
        tencentToken.current = ''
        tencentRandstr.current = ''
    }

    const doSubmit = (formData: object) => {
        // Decorate here so both CAPTCHA paths (Google and Tencent) carry the product context.
        // colorCode is trimmed to match the Form API contract (leading/trailing spaces removed).
        const decoratedFormData = {
            ...formData,
            sku: productId,
            ecomSize: variant?.size.label,
            model: variant?.model,
            colorCode: variant?.color.code?.trim(),
        }
        showLoader()
        submitFormMutation({locale: locale.id, formId: 'requestSize', formData: decoratedFormData})
            .then((data: SubmitRequestSizeResponse) => {
                // Per the Form API contract, success is signalled by a top-level `success: true` flag
                // ({ success, status, message }). A failed CAPTCHA (and any other failure) returns HTTP 200
                // with `success: false` — same convention as the PWA Newsletter — so it must be treated as
                // an error. The legacy nested `response.success` shape is still accepted for compatibility.
                const isSuccess = (data as {success?: boolean})?.success === true || ('response' in data && data.response?.success === true)
                if (isSuccess) {
                    setStepState(() => 2)
                    analyticsEventRequestSize({action: 'submit_ok', label: variant?.ID.slice(0, -2) + ' - ' + variant?.size.label})
                    onSubmitForm()
                } else {
                    setIsSubmitError(() => true)
                    analyticsEventRequestSize({action: 'submit_fail', label: variant?.ID.slice(0, -2) + ' - ' + variant?.size.label})
                }
            })
            .catch((error) => {
                setIsSubmitError(() => true)
                analyticsEventRequestSize({action: 'submit_fail', label: variant?.ID.slice(0, -2) + ' - ' + variant?.size.label})
            })
            .finally(() => {
                hideLoader()
                resetCaptchaTokens()
                resetTencentCaptcha()
            })
    }

    const submitRequestSize = (data) => {
        if (isTencentEnabled) {
            pendingFormData.current = data
            triggerTencentValidation()
            return
        }
        const formData = isRecaptchaActive ? {...data, 'g-recaptcha-response': recaptchaToken.current} : data
        doSubmit(formData)
    }

    useEffect(() => {
        if (isTencentEnabled && tencentCaptchaResult) {
            tencentToken.current = tencentCaptchaResult.ticket
            tencentRandstr.current = tencentCaptchaResult.randstr

            if (pendingFormData.current) {
                const formData = {...pendingFormData.current, tencent_captcha_ticket: tencentToken.current, tencent_captcha_randstr: tencentRandstr.current}
                pendingFormData.current = null
                doSubmit(formData)
            }
        } else if (!isTencentEnabled) {
            void handleReCaptchaVerify()
        }
    }, [tencentCaptchaResult, isTencentEnabled])

    useEffect(() => {
        getFormMutation({locale: locale.id, formId: 'requestSize'})
            .then((response) => setRequestSizeForm(() => response.form))
            .catch(() => {})
            .finally(() => {})

        const initialSelectedVariant = productId ? variantsNotAvailable.find((v) => v.ID === productId) || null : null
        setVariant(initialSelectedVariant)
        if (initialSelectedVariant?.size.code) {
            setValue('size', initialSelectedVariant.size.code, {shouldValidate: false, shouldDirty: false})
        }
    }, [])

    return (
        <VStack {...styles.container}>
            {stepState === 1 && (
                <VStack {...styles.textContainer}>
                    <Text {...styles.subtitle}>
                        <FormattedMessage
                            id="label.requestsize.subtitle.text"
                            defaultMessage={'Fill out the form and you will be notified when the size you want is available again.'}
                        />
                    </Text>
                    <Text {...styles.requiredFieldsText}>
                        <FormattedMessage id="label.contact.form.required.fields" defaultMessage={'Fields marked with an asterisk (*) are required'} />
                    </Text>
                </VStack>
            )}

            {stepState === 1 && requestSizeForm && !isSubmitError && (
                <VStack {...styles.inputContainer} as="form" noValidate onSubmit={handleSubmit((data) => submitRequestSize(data))}>
                    <FormFactory
                        type="select"
                        control={control}
                        errors={errors}
                        isRequired={true}
                        label={formatMessage({id: 'label.pdp.select.size', defaultMessage: 'Select size'})}
                        name={'size'}
                        value={variant?.size.code || ''}
                        options={variantsNotAvailable.map((variant, index) => ({label: variant.size.label || '', value: variant.size.code || '', index})) || []}
                        placeholder={formatMessage({id: 'resource.select', defaultMessage: 'Select...'})}
                        onChange={(e) => {
                            const selectedVariant = variantsNotAvailable.find((v) => v.size.code === e) || null
                            setVariant(selectedVariant)
                        }}
                    />

                    <FormFactory
                        type="select"
                        control={control}
                        errors={errors}
                        name={'gender'}
                        label={requestSizeForm.requestSize.gender.label}
                        value={''}
                        options={requestSizeForm.requestSize.gender.options?.map((option, index) => ({label: option.label, value: option.id, index})) || []}
                        isRequired={false}
                        maxLength={requestSizeForm.requestSize.gender.maxLength}
                    />

                    <FormFactory
                        type="text"
                        control={control}
                        errors={errors}
                        name={'firstname'}
                        label={requestSizeForm.requestSize.firstname.label}
                        isRequired={requestSizeForm.requestSize.firstname.mandatory}
                        maxLength={requestSizeForm.requestSize.firstname.maxLength}
                    />

                    <FormFactory
                        type="text"
                        control={control}
                        errors={errors}
                        name={'lastname'}
                        label={requestSizeForm.requestSize.lastname.label}
                        isRequired={requestSizeForm.requestSize.lastname.mandatory}
                        maxLength={requestSizeForm.requestSize.lastname.maxLength}
                    />

                    <FormFactory
                        type="email"
                        control={control}
                        errors={errors}
                        name={'email'}
                        label={requestSizeForm.requestSize.email.label}
                        isRequired={requestSizeForm.requestSize.email.mandatory}
                        maxLength={requestSizeForm.requestSize.email.maxLength}
                        pattern={requestSizeForm.requestSize.email.regEx}
                    />

                    <FormPrivacyDisclaimer
                        privacyMessageProps={styles.privacyMessage}
                        recaptchaMessageProps={styles.recaptchaMessage}
                        containerProps={styles.privacyContainer}
                    />
                    <Button variant="primary" type="submit">
                        <FormattedMessage id="button.offcanvas.request.size" defaultMessage={'Request product'} />
                    </Button>
                </VStack>
            )}
            {(!requestSizeForm || requestSizeForm === undefined) && stepState === 1 && <ProductRequestSizeSkeleton />}
            {isSubmitError && stepState === 1 && (
                <Text {...styles.errorMessage}>
                    <FormattedMessage id="label.pdp.requestSize.fail" defaultMessage={'An error has occurred. Please try again later'} />
                </Text>
            )}
            {stepState === 2 && (
                <VStack {...styles.successContainer}>
                    <Text {...styles.successMessage}>
                        <FormattedMessage id="label.form.success.message" defaultMessage={'You will receive updates via e-mail as soon as possible.'} />
                    </Text>
                </VStack>
            )}
        </VStack>
    )
}

export default ProductRequestSize

/**
 * @name ProductRequestSizeSkeleton
 *
 * @component
 *
 * @description
 * Renders skeleton for the ProductRequestSize component.
 *
 * @returns {JSX.Element} - Rendered ProductRequestSizeSkeleton component.
 */
const ProductRequestSizeSkeleton = (): JSX.Element => {
    const styles = useMultiStyleConfig('ProductRequestSize', {}) as Record<string, BoxProps>
    return (
        <VStack {...styles.inputContainer} data-testid="product-request-size-skeleton">
            {Array.from({length: 5}).map((_, i) => (
                <Box w={'100%'} mb={'spacing.1'} key={i}>
                    <Skeleton height="17px" mb={'spacing.1'} width="40%" />
                    <Skeleton height="54px" mb={'spacing.1'} width="100%" />
                    <Skeleton height="17px" mb={'spacing.1'} width="50%" />
                </Box>
            ))}

            <Skeleton height="17px" mb={'spacing.1'} width="100%" />
            <Skeleton height="48px" mb={'spacing.1'} width="100%" />
        </VStack>
    )
}
